import { useState } from "react";
import Modal from "react-modal";
import * as S from "./styles";
import Close from "assets/close-small.svg";
import { IGetByIdPoliceman } from "interfaces/Policeman";
import Input from "../../Input";
import { ApprovalButton, Button, RefuseButton } from "components/Button";
import { approvePolicemen } from "services/PolicemenServices";

import { ReactComponent as CarIcon } from "assets/car.svg";
import { ReactComponent as HomeIcon } from "assets/home.svg";
import { CustomIconedHeader } from "./styles";
import AttachmentsGallery from "components/AttachmentsGallery";

interface ModalProps {
  modalIsOpen: boolean;
  setIsOpen: (value: boolean) => void;
  policeman: IGetByIdPoliceman;
}

interface FormElements extends HTMLFormControlsCollection {
  vehicle_additional_km: HTMLInputElement;
  vehicle_additional_hour: HTMLInputElement;
  vehicle_exit_value: HTMLInputElement;
  vehicle_mileageKm: HTMLInputElement;
  additional_km: HTMLInputElement;
  additional_hour: HTMLInputElement;
  exit_value: HTMLInputElement;
  mileageKm: HTMLInputElement;
}

interface PolicemanPaymentForm extends HTMLFormElement {
  readonly elements: FormElements;
}

export function MorePolicemanDetailsModal({
  modalIsOpen,
  setIsOpen,
  policeman,
}: ModalProps) {
  const [isRequesting, setIsRequesting] = useState<boolean>(false);

  const policemanApproved = policeman.policeman.approved;

  function closeModal() {
    setIsOpen(false);
  }

  const types = [
    "Usuário PRMA",
    "Fornecedor",
    "Policia",
    "Cliente",
    "Funcionário de Cliente",
    "Funcionário de Fornecedor",
  ];

  async function handleSubmit(event: React.FormEvent<PolicemanPaymentForm>) {
    event.preventDefault();

    const patrimonialValues = {
      additionalKm: Number(event.currentTarget.elements.additional_km.value),
      additionalHour: Number(
        event.currentTarget.elements.additional_hour.value
      ),
      mileageKm: Number(event.currentTarget.elements.mileageKm.value),
      exitValue: Number(event.currentTarget.elements.exit_value.value),
    };

    const vehicleValues = {
      additionalKm: Number(
        event.currentTarget.elements.vehicle_additional_km.value
      ),
      additionalHour: Number(
        event.currentTarget.elements.vehicle_additional_hour.value
      ),
      exitValue: Number(event.currentTarget.elements.vehicle_exit_value.value),
      mileageKm: Number(event.currentTarget.elements.vehicle_mileageKm.value),
    };

    const paymentValues = {
      patrimonialValues,
      vehicleValues,
    };

    setIsRequesting(true);

    const { id } = policeman.policeman;

    await approvePolicemen({
      id,
      approve: true,
      paymentValues,
    }).finally(() => {
      setIsRequesting(false);
    });
  }

  async function handlePolicemanRejection() {
    setIsRequesting(true);
    const { id } = policeman.policeman;
    return await approvePolicemen({
      id,
      approve: false,
    });
  }

  return (
    <Modal
      isOpen={modalIsOpen}
      onRequestClose={closeModal}
      contentLabel="MorePolicemanDetails Modal"
      contentElement={(props, children) => (
        <S.MorePolicemanModalContent
          {...props}
          isApprovalModal={!policemanApproved}
        >
          {children}
        </S.MorePolicemanModalContent>
      )}
      overlayElement={(props, contentElement) => (
        <S.MorePolicemanModalOverlay {...props}>
          {contentElement}
        </S.MorePolicemanModalOverlay>
      )}
    >
      <S.Container>
        <S.MorePolicemanHeader>
          <header>
            <h2>{policemanApproved ? "Dados do agente" : "Aprovar agente"}</h2>
            <img src={Close} alt="fechar" onClick={closeModal} />
          </header>
        </S.MorePolicemanHeader>
        <S.MorePolicemanDetailsContent approved={policemanApproved}>
          <S.MorePolicemanContainer>
            <S.BadgeContainer>
              <span>
                <label>Efetuou o cadastro em</label>
                <p>
                  {new Date(policeman.createdAt).toLocaleDateString("pt-BR", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </p>
              </span>
            </S.BadgeContainer>

            <S.DataContainer>
              <h3>Dados</h3>
              <S.MorePolicemanContent>
                <span>
                  <label>Nome do pronta-resposta</label>
                  <p>{policeman?.name}</p>
                </span>
                <span>
                  <label>Contato</label>
                  <p>{policeman?.contact?.phoneNumber}</p>
                </span>
                <span>
                  <label>RG</label>
                  <p>{policeman?.rg}</p>
                </span>
                <span>
                  <label>Tipo de usuário</label>
                  {policeman?.type && <p>{types[policeman?.type - 1]}</p>}
                </span>
                <span>
                  <label>CPF</label>
                  <p>{policeman?.cpf}</p>
                </span>
                <span>
                  <label>E-mail</label>
                  <p>{policeman?.email}</p>
                </span>
                <span>
                  <label>Veículo</label>
                  <p>{policeman?.vehicle?.model}</p>
                </span>
                <span>
                  <label>Placa</label>
                  <p>{policeman?.vehicle?.plate}</p>
                </span>
                <span>
                  <label>Endereço</label>
                  <p>{`${policeman?.address.street}, ${policeman.address.number} - ${policeman.address.district}, ${policeman.address.city} - ${policeman.address.uf}, ${policeman.address.postalCode}`}</p>
                </span>
              </S.MorePolicemanContent>
            </S.DataContainer>

            <S.DataContainer>
              <h3>Sobre o agente</h3>
              <S.MorePolicemanContent>
                <span>
                  <label>Região preferida</label>
                  <p>{policeman?.policeman.region}</p>
                </span>
                <span>
                  <label>É agente público?</label>
                  <p>{policeman?.policeman.publicAgent ? "Sim" : "Não"}</p>
                </span>
                <span>
                  <label>Está na ativa?</label>
                  <p>{policeman?.policeman.working ? "Sim" : "Não"}</p>
                </span>
              </S.MorePolicemanContent>
            </S.DataContainer>

            <S.DataContainer>
              <h3>Conta bancária</h3>
              <S.MorePolicemanContent>
                <span>
                  <label>Chave PIX</label>
                  <p>
                    {policeman?.bankAccount?.accountData?.pix ||
                      "Não informado"}
                  </p>
                </span>
                <span>
                  <label>Conta</label>
                  <p>{policeman?.bankAccount?.accountData?.account}</p>
                </span>
                <span>
                  <label>Agência</label>
                  <p>{policeman?.bankAccount?.accountData?.agency}</p>
                </span>
                {policemanApproved && (
                  <span>
                    <label>CNPJ</label>
                    <p>{policeman?.cnpj || "Não informado"}</p>
                  </span>
                )}
              </S.MorePolicemanContent>
            </S.DataContainer>

            <S.DataContainer>
              <h3>Fotos de registro</h3>
              <S.PolicemanPictures>
                <AttachmentsGallery
                  photoGallery={Object.entries(
                    policeman.policeman.pictures
                  ).map(([photoType, photoUrl]) => ({
                    active: true,
                    category: photoType,
                    createdAt: new Date(),
                    id: "",
                    location: photoType,
                    report: "",
                    storageReference: policeman.policeman.id,
                    updatedAt: new Date(),
                    url: photoUrl,
                    caption: photoType,
                  }))}
                />
              </S.PolicemanPictures>
            </S.DataContainer>
          </S.MorePolicemanContainer>

          {!policemanApproved && (
            <S.Column onSubmit={handleSubmit}>
              <S.Body>
                <S.Header>
                  <h3>Adicionar valores de pagamento</h3>
                  <span>
                    Antes de aprovar o pronta-resposta, insira os valores de
                    pagamento (para ocorrências veiculares e patrimoniais)
                  </span>
                </S.Header>

                <S.FormBody>
                  <CustomIconedHeader
                    icon={<CarIcon />}
                    title="Para ocorrências veiculares"
                  />

                  <S.FormGroup>
                    <Input
                      id="vehicle_additional_km"
                      name="vehicle_additional_km"
                      required
                      type="number"
                      label="Valor por KM adicional"
                    />
                  </S.FormGroup>

                  <S.FormGroup>
                    <Input
                      id="vehicle_additional_hour"
                      name="vehicle_additional_hour"
                      required
                      type="number"
                      label="Valor por hora adicional"
                    />
                  </S.FormGroup>

                  <S.FormGroup>
                    <Input
                      id="vehicle_exit_value"
                      name="vehicle_exit_value"
                      required
                      type="number"
                      label="Valor de saida do PR"
                    />
                  </S.FormGroup>
                  <S.FormGroup>
                    <Input
                      id="vehicle_mileageKm"
                      name="vehicle_mileageKm"
                      required
                      type="number"
                      label="Valor de franquia por KM"
                    />
                  </S.FormGroup>
                </S.FormBody>

                <hr />

                <S.FormBody>
                  <CustomIconedHeader
                    icon={<HomeIcon />}
                    title="Para ocorrências patrimoniais"
                  />

                  <S.FormGroup>
                    <Input
                      id="additional_km"
                      name="additional_km"
                      required
                      type="number"
                      label="Valor por KM adicional"
                    />
                  </S.FormGroup>

                  <S.FormGroup>
                    <Input
                      id="additional_hour"
                      name="additional_hour"
                      required
                      type="number"
                      label="Valor por hora adicional"
                    />
                  </S.FormGroup>

                  <S.FormGroup>
                    <Input
                      id="exit_value"
                      name="exit_value"
                      required
                      type="number"
                      label="Valor de saida do PR"
                    />
                  </S.FormGroup>
                  <S.FormGroup>
                    <Input
                      id="mileageKm"
                      name="mileageKm"
                      required
                      type="number"
                      label="Valor de franquia por KM"
                    />
                  </S.FormGroup>
                </S.FormBody>
              </S.Body>

              <S.Footer>
                <S.FooterFormGroup>
                  <ApprovalButton
                    disabled={isRequesting}
                    title="Aprovar pronta-resposta"
                    type="submit"
                  />
                  <Button
                    variant="error"
                    disabled={isRequesting}
                    title="Reprovar e excluir"
                    type="button"
                    onClick={handlePolicemanRejection}
                  />
                </S.FooterFormGroup>
              </S.Footer>
            </S.Column>
          )}
        </S.MorePolicemanDetailsContent>
      </S.Container>
    </Modal>
  );
}
